/**
 * L'objectif du jour, toujours visible — RG-2.41, 06 §RG-D23.
 *
 * **Il flotte parce qu'un compteur en haut de page n'existe plus au bout de
 * trois cartes.** Le Fil se défile ; l'objectif ne sert que s'il reste sous
 * l'œil au moment où l'on décide de continuer ou d'arrêter. C'est exactement
 * ce moment qu'il vise.
 *
 * **Seules les réponses du FIL le font avancer.** Ni les marque-pages, ni « à
 * revoir » : l'objectif mesure une séance de révision, pas un ménage dans ses
 * listes. Compter les marque-pages permettrait de l'atteindre en relisant dix
 * questions déjà connues, ce qui viderait le chiffre de son sens — et c'est
 * l'appelant qui décide de le montrer, puisque lui seul sait dans quelle liste
 * on se trouve.
 *
 * **Il occupe la place du bouton flottant de Twitter** : en bas à droite de
 * l'application, au-dessus de la barre d'onglets sur petit écran. C'est
 * exactement là où se trouvait le bouton « Réviser » avant son retrait
 * (`bottom-[69px] right-4`), et le coin est libre depuis.
 *
 * **Borné à la COQUE, jamais au bord de la fenêtre** (RG-2.41 précisée,
 * [DECISIONS §35]). En `right: 16px` nu, il se posait à 423 px à DROITE de
 * l'application sur un écran de 1680 px : dans la marge vide, à côté du produit
 * plutôt que dedans. Techniquement visible, pratiquement absent — rien n'attire
 * l'œil vers un coin où il ne se passe jamais rien.
 *
 * **Comment.** Un `max()` en CSS pur, sans une ligne de JavaScript :
 *
 *     right: max(1rem, calc((100% - 1265px) / 2 + 1rem))
 *
 * Sous 1265 px — la largeur maximale de la coque — la marge est négative et
 * `max()` retient 1 rem : le coin de la fenêtre EST celui de l'application, et
 * la pastille tombe exactement où tombait le bouton flottant. Au-delà, elle
 * suit le bord droit de la coque. Le pourcentage se résout sur le bloc
 * conteneur initial, qui exclut la barre de défilement : le décalage reste
 * juste sans qu'on ait à mesurer quoi que ce soit.
 *
 * L'alternative — lire `getBoundingClientRect` à chaque redimensionnement —
 * ferait sauter la pastille d'un cran à chaque rendu pour un résultat que cette
 * seule ligne obtient.
 *
 * **Rempli de la couleur du thème.** En blanc sur fond blanc, il fallait le
 * chercher — et un objectif qu'on ne voit pas ne fait revenir personne.
 */
import { useEffect, useRef } from 'react'

import { sonObjectifAtteint } from '@/lib/son'

/**
 * La largeur maximale de la coque — le `max-w-[1265px]` d'`AppShell`.
 *
 * Recopiée ici parce que Tailwind ne l'expose pas au JavaScript. Si elle change
 * là-bas, elle doit changer ici : la pastille se poserait sinon à côté du bord
 * qu'elle est censée suivre.
 */
const LARGEUR_DE_LA_COQUE = '1265px'

export function ObjectifFlottant({
  fait,
  objectif,
  serie = 0,
}: {
  fait: number
  objectif: number
  /**
   * La série en cours — affichée SEULEMENT une fois l'objectif atteint, §83.
   *
   * C'est elle qui est en jeu (RG-P17) : atteindre son objectif la garde,
   * le manquer la perd. La dire au moment exact où elle est sauvée est la
   * seule information réellement motivante du produit. L'annoncer avant, en
   * revanche, serait une menace : une série encore en danger.
   */
  serie?: number
}) {
  // ── LE SON DU FRANCHISSEMENT — §392 ──────────────────────────────────────
  //
  // **À la BASCULE, jamais au rendu.** `atteint` est une valeur dérivée : elle
  // reste vraie à tous les rendus qui suivent. Un `sonObjectifAtteint()` posé
  // dans le corps sonnerait à chaque carte du reste de la journée.
  //
  // Le repère part à la valeur du montage : quelqu'un qui recharge la page
  // après avoir atteint son objectif ne doit pas l'entendre une seconde fois.
  //
  // Et le calcul vit AVANT le garde `objectif <= 0` : un `return` précoce
  // placé au-dessus d'un `useEffect` rompt l'ordre des hooks.
  const franchi = objectif > 0 && fait >= objectif
  const dejaFranchi = useRef(franchi)

  useEffect(() => {
    if (franchi && !dejaFranchi.current) {
      sonObjectifAtteint()
    }

    dejaFranchi.current = franchi
  }, [franchi])

  if (objectif <= 0) return null

  // **Le compteur AFFICHÉ avance par cinq** — §150. Il compte des bonnes
  // réponses et les compte toutes, mais ne montre que les multiples de cinq :
  // « 20 » veut dire vingt bonnes réponses données aujourd'hui. Sans ce
  // plancher, la pastille bouge à chaque carte et devient un bruit qu'on cesse
  // de regarder ; avec lui, elle marque des étapes.
  //
  // **Une fois l'objectif atteint, on montre le compte exact** : arrondir vers
  // le bas afficherait « 48 / 50 » à quelqu'un qui a fini, et lui ferait croire
  // qu'il lui en reste deux.
  const atteint = fait >= objectif
  const montre = atteint ? fait : Math.floor(fait / 5) * 5
  const part = Math.min(1, fait / objectif)

  // Le tour du cercle : 2πr avec r = 20.
  const tour = 2 * Math.PI * 20

  return (
    <div
      // `pointer-events-none` : la pastille informe, elle ne se clique pas.
      // Sans cela, elle avalerait les tapes sur la carte qu'elle recouvre.
      //
      // `bottom-[69px]` la pose juste au-dessus de la barre d'onglets (53 px)
      // sur petit écran — la place exacte du bouton flottant de Twitter, et
      // celle qu'occupait le bouton « Réviser ». `bottom-6` là où la barre
      // n'existe pas.
      className="pointer-events-none fixed bottom-[69px] z-20 flex items-center gap-2 rounded-full px-3 py-2 shadow-lg min-[688px]:bottom-6"
      style={{
        // Le coin bas-droit de la COQUE, jamais celui de la fenêtre. Sous
        // 1265 px les deux se confondent et `max()` retient 1 rem ; au-delà,
        // la pastille suit le bord de l'application au lieu de dériver dans la
        // marge vide. `LARGEUR_DE_LA_COQUE` doit rester égale au `max-w` de
        // `AppShell` : c'est la seule chose que cette ligne suppose.
        right: `max(1rem, calc((100% - ${LARGEUR_DE_LA_COQUE}) / 2 + 1rem))`,
        background: 'var(--accent-fort)',
        color: 'var(--sur-accent)',
      }}
      role="status"
      aria-label={
        atteint
          ? `Objectif du jour atteint : ${String(montre)} bonnes réponses sur ${String(objectif)}`
            + (serie > 0 ? `, série de ${String(serie)} jours gardée` : '')
          : `Objectif du jour : ${String(montre)} bonnes réponses sur ${String(objectif)}`
      }
    >
      <svg viewBox="0 0 48 48" width={34} height={34} aria-hidden="true">
        {/* Sur fond d'accent, l'anneau se lit en blanc translucide puis
            plein : un anneau à la couleur d'accent y serait invisible. */}
        <circle
          cx="24"
          cy="24"
          r="20"
          fill="none"
          stroke="color-mix(in srgb, var(--sur-accent) 35%, transparent)"
          strokeWidth="5"
        />
        <circle
          cx="24"
          cy="24"
          r="20"
          fill="none"
          stroke="var(--sur-accent)"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={tour}
          strokeDashoffset={tour * (1 - part)}
          // Le cercle part du HAUT : un anneau qui se remplit depuis la
          // droite se lit comme une horloge cassée.
          transform="rotate(-90 24 24)"
          style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
        />

        {/* La coche, une fois l'anneau plein — §83. L'anneau seul, à 100 %, ne
            se distingue pas d'un anneau à 97 % : c'est la coche qui dit que
            c'est fait. */}
        {atteint && (
          <path
            d="M15 24.5 21 30.5 33 18.5"
            fill="none"
            stroke="var(--sur-accent)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>

      <span className="text-[13px] font-bold leading-tight">
        {/* **Au-delà de l'objectif, on compte TOUJOURS** — §83. Masquer le
            compteur une fois la cible franchie dirait que le travail
            supplémentaire ne compte pas ; le remettre à zéro, pire encore. */}
        {atteint ? (
          <>
            {serie > 0 ? `Série de ${String(serie)} gardée` : 'Objectif atteint'}
            <br />
            <span className="font-normal opacity-80">
              {montre} / {objectif}
            </span>
          </>
        ) : (
          <>
            {montre} / {objectif}
            <br />
            {/* « du jour » plutôt qu'« aujourd'hui » : sept signes contre
                onze, sur la ligne qui fixe la largeur de la pastille — elle
                flotte au-dessus des cartes, et chaque point qu'elle prend est
                repris au contenu. Le mot est celui du rail, « Ton objectif du
                jour » : même vocabulaire, pas une abréviation. */}
            <span className="font-normal opacity-80">du jour</span>
          </>
        )}
      </span>
    </div>
  )
}
